// Tipos para el módulo Marketing de InboMarket
// Basados en los endpoints del backend documentados

// === ENUMS ===

export enum CampaignStatus {
  DRAFT = 'draft',
  SCHEDULED = 'scheduled',
  RUNNING = 'running',
  PAUSED = 'paused',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  FAILED = 'failed',
}

export enum CampaignType {
  PROMOTIONAL = 'promotional',
  INFORMATIONAL = 'informational',
  NEWSLETTER = 'newsletter',
  FOLLOW_UP = 'follow_up',
  PROPERTY_ALERT = 'property_alert',
  MARKET_UPDATE = 'market_update',
  EVENT_INVITATION = 'event_invitation',
  SURVEY = 'survey',
  OTHER = 'other',
}

export enum EmailTemplate {
  WELCOME = 'welcome',
  PROPERTY_SHOWCASE = 'property_showcase',
  MARKET_REPORT = 'market_report',
  APPOINTMENT_REMINDER = 'appointment_reminder',
  FOLLOW_UP = 'follow_up',
  NEWSLETTER = 'newsletter',
  CUSTOM = 'custom',
}

export enum WhatsAppMessageType {
  TEXT = 'text',
  IMAGE = 'image',
  DOCUMENT = 'document',
  TEMPLATE = 'template',
  INTERACTIVE = 'interactive',
}

export enum TargetAudience {
  ALL_LEADS = 'all_leads',
  ACTIVE_LEADS = 'active_leads',
  HOT_LEADS = 'hot_leads',
  COLD_LEADS = 'cold_leads',
  CLIENTS = 'clients',
  PROSPECTS = 'prospects',
  CUSTOM = 'custom',
}

export enum CampaignPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

// === INTERFACES BASE ===

export interface EmailCampaignDTO {
  id: string;
  name: string;
  subject: string;
  description?: string;

  // Contenido
  template: EmailTemplate;
  htmlContent: string;
  textContent?: string;
  previewText?: string;

  // Configuración
  type: CampaignType;
  status: CampaignStatus;
  priority: CampaignPriority;

  // Audiencia
  targetAudience: TargetAudience;
  recipientsList: string[]; // IDs de leads/contacts
  segmentationCriteria?: SegmentationCriteria;

  // Programación
  scheduledAt?: Date;
  sentAt?: Date;
  timezone: string;

  // Configuración de envío
  senderName: string;
  senderEmail: string;
  replyToEmail?: string;
  trackOpens: boolean;
  trackClicks: boolean;

  // Estadísticas
  stats: EmailCampaignStats;

  // Metadatos
  tags: string[];
  tenantId: string;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface WhatsAppCampaignDTO {
  id: string;
  name: string;
  description?: string;

  // Contenido
  messageType: WhatsAppMessageType;
  messageContent: string;
  mediaUrl?: string;
  templateName?: string;
  templateParams?: Record<string, string>;

  // Configuración
  type: CampaignType;
  status: CampaignStatus;
  priority: CampaignPriority;

  // Audiencia
  targetAudience: TargetAudience;
  recipientsList: string[]; // Números de teléfono
  segmentationCriteria?: SegmentationCriteria;

  // Programación
  scheduledAt?: Date;
  sentAt?: Date;
  timezone: string;

  // Configuración de WhatsApp
  businessPhoneNumberId: string;
  enableDeliveryReceipts: boolean;
  enableReadReceipts: boolean;

  // Estadísticas
  stats: WhatsAppCampaignStats;

  // Metadatos
  tags: string[];
  tenantId: string;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

// === INTERFACES DE SOPORTE ===

export interface SegmentationCriteria {
  leadStatus?: string[];
  leadSource?: string[];
  propertyInterests?: string[];
  budgetRange?: {
    min: number;
    max: number;
  };
  locations?: string[];
  agentIds?: string[];
  tags?: string[];
  lastInteractionDays?: number;
  createdInLastDays?: number;
}

export interface EmailCampaignStats {
  totalSent: number;
  delivered: number;
  bounced: number;
  opened: number;
  clicked: number;
  unsubscribed: number;
  marked_as_spam: number;

  // Ratios
  deliveryRate: number; // %
  openRate: number; // %
  clickRate: number; // %
  unsubscribeRate: number; // %
  spamRate: number; // %

  // Engagement
  uniqueOpens: number;
  uniqueClicks: number;
  clickToOpenRate: number; // %

  // Timing
  firstOpenAt?: Date;
  lastOpenAt?: Date;
  averageOpenTime?: number; // minutos después del envío
}

export interface WhatsAppCampaignStats {
  totalSent: number;
  delivered: number;
  failed: number;
  read: number;
  replied: number;

  // Ratios
  deliveryRate: number; // %
  readRate: number; // %
  replyRate: number; // %

  // Timing
  firstReadAt?: Date;
  lastReadAt?: Date;
  averageResponseTime?: number; // minutos

  // Engagement
  clicksGenerated: number;
  leadsGenerated: number;
  conversions: number;
}

export interface CampaignRecipient {
  id: string;
  contactId: string;
  contactName: string;
  contactEmail?: string;
  contactPhone?: string;

  // Status del envío
  sentAt?: Date;
  deliveredAt?: Date;
  openedAt?: Date;
  clickedAt?: Date;

  // Email específico
  bounceReason?: string;
  unsubscribedAt?: Date;

  // WhatsApp específico
  readAt?: Date;
  repliedAt?: Date;
  failureReason?: string;
}

// === DTOs PARA CREATE ===

export interface EmailCampaignCreateDTO {
  name: string;
  subject: string;
  description?: string;
  template: EmailTemplate;
  htmlContent: string;
  textContent?: string;
  previewText?: string;
  type: CampaignType;
  priority?: CampaignPriority;
  targetAudience: TargetAudience;
  recipientsList?: string[];
  segmentationCriteria?: SegmentationCriteria;
  scheduledAt?: Date;
  timezone?: string;
  senderName: string;
  senderEmail: string;
  replyToEmail?: string;
  trackOpens?: boolean;
  trackClicks?: boolean;
  tags?: string[];
}

export interface WhatsAppCampaignCreateDTO {
  name: string;
  description?: string;
  messageType: WhatsAppMessageType;
  messageContent: string;
  mediaUrl?: string;
  templateName?: string;
  templateParams?: Record<string, string>;
  type: CampaignType;
  priority?: CampaignPriority;
  targetAudience: TargetAudience;
  recipientsList?: string[];
  segmentationCriteria?: SegmentationCriteria;
  scheduledAt?: Date;
  timezone?: string;
  businessPhoneNumberId: string;
  enableDeliveryReceipts?: boolean;
  enableReadReceipts?: boolean;
  tags?: string[];
}

// === DTOs PARA UPDATE ===

export interface EmailCampaignUpdateDTO {
  name?: string;
  subject?: string;
  description?: string;
  template?: EmailTemplate;
  htmlContent?: string;
  textContent?: string;
  previewText?: string;
  type?: CampaignType;
  status?: CampaignStatus;
  priority?: CampaignPriority;
  targetAudience?: TargetAudience;
  recipientsList?: string[];
  segmentationCriteria?: SegmentationCriteria;
  scheduledAt?: Date;
  timezone?: string;
  senderName?: string;
  senderEmail?: string;
  replyToEmail?: string;
  trackOpens?: boolean;
  trackClicks?: boolean;
  tags?: string[];
}

export interface WhatsAppCampaignUpdateDTO {
  name?: string;
  description?: string;
  messageType?: WhatsAppMessageType;
  messageContent?: string;
  mediaUrl?: string;
  templateName?: string;
  templateParams?: Record<string, string>;
  type?: CampaignType;
  status?: CampaignStatus;
  priority?: CampaignPriority;
  targetAudience?: TargetAudience;
  recipientsList?: string[];
  segmentationCriteria?: SegmentationCriteria;
  scheduledAt?: Date;
  timezone?: string;
  businessPhoneNumberId?: string;
  enableDeliveryReceipts?: boolean;
  enableReadReceipts?: boolean;
  tags?: string[];
}

// === FILTROS ===

export interface EmailCampaignFilterDTO {
  search?: string;
  status?: CampaignStatus;
  type?: CampaignType;
  template?: EmailTemplate;
  priority?: CampaignPriority;
  targetAudience?: TargetAudience;
  createdBy?: string;
  createdFrom?: Date;
  createdTo?: Date;
  scheduledFrom?: Date;
  scheduledTo?: Date;
  tags?: string[];
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface WhatsAppCampaignFilterDTO {
  search?: string;
  status?: CampaignStatus;
  type?: CampaignType;
  messageType?: WhatsAppMessageType;
  priority?: CampaignPriority;
  targetAudience?: TargetAudience;
  createdBy?: string;
  createdFrom?: Date;
  createdTo?: Date;
  scheduledFrom?: Date;
  scheduledTo?: Date;
  tags?: string[];
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// === ESTADÍSTICAS GENERALES ===

export interface EmailCampaignStatsDTO {
  totalCampaigns: number;
  activeCampaigns: number;
  scheduledCampaigns: number;
  completedCampaigns: number;

  totalEmailsSent: number;
  averageOpenRate: number;
  averageClickRate: number;
  averageDeliveryRate: number;

  thisMonth: {
    campaigns: number;
    emailsSent: number;
    leads: number;
    conversions: number;
  };

  topPerformingCampaigns: Array<{
    id: string;
    name: string;
    openRate: number;
    clickRate: number;
    leadsGenerated: number;
  }>;

  performanceByMonth: Array<{
    month: string;
    campaigns: number;
    emailsSent: number;
    openRate: number;
    clickRate: number;
  }>;
}

export interface WhatsAppCampaignStatsDTO {
  totalCampaigns: number;
  activeCampaigns: number;
  scheduledCampaigns: number;
  completedCampaigns: number;

  totalMessagesSent: number;
  averageDeliveryRate: number;
  averageReadRate: number;
  averageReplyRate: number;

  thisMonth: {
    campaigns: number;
    messagesSent: number;
    leads: number;
    conversions: number;
  };

  topPerformingCampaigns: Array<{
    id: string;
    name: string;
    deliveryRate: number;
    readRate: number;
    replyRate: number;
  }>;

  performanceByMonth: Array<{
    month: string;
    campaigns: number;
    messagesSent: number;
    deliveryRate: number;
    readRate: number;
  }>;
}

// === TEMPLATES Y CONTENIDO ===

export interface EmailTemplateDTO {
  id: string;
  name: string;
  description?: string;
  category: EmailTemplate;
  subject: string;
  htmlContent: string;
  textContent?: string;
  previewImage?: string;
  variables: TemplateVariable[];
  isActive: boolean;
  tenantId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface WhatsAppTemplateDTO {
  id: string;
  name: string;
  description?: string;
  category: string;
  language: string;
  content: string;
  variables: TemplateVariable[];
  status: 'pending' | 'approved' | 'rejected';
  whatsappTemplateId?: string;
  tenantId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface TemplateVariable {
  name: string;
  type: 'text' | 'number' | 'date' | 'url' | 'phone' | 'email';
  description: string;
  required: boolean;
  defaultValue?: string;
  example?: string;
}

// === RESPUESTAS PAGINADAS ===

export interface EmailCampaignListResponse {
  campaigns: EmailCampaignDTO[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface WhatsAppCampaignListResponse {
  campaigns: WhatsAppCampaignDTO[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// === WEBHOOK TYPES ===

export interface WhatsAppWebhookEvent {
  object: string;
  entry: WhatsAppWebhookEntry[];
}

export interface WhatsAppWebhookEntry {
  id: string;
  changes: WhatsAppWebhookChange[];
}

export interface WhatsAppWebhookChange {
  value: {
    messaging_product: string;
    metadata: {
      display_phone_number: string;
      phone_number_id: string;
    };
    contacts?: WhatsAppContact[];
    messages?: WhatsAppMessage[];
    statuses?: WhatsAppStatus[];
  };
  field: string;
}

export interface WhatsAppContact {
  profile: {
    name: string;
  };
  wa_id: string;
}

export interface WhatsAppMessage {
  from: string;
  id: string;
  timestamp: string;
  text?: {
    body: string;
  };
  type: string;
}

export interface WhatsAppStatus {
  id: string;
  status: 'delivered' | 'read' | 'sent' | 'failed';
  timestamp: string;
  recipient_id: string;
  errors?: Array<{
    code: number;
    title: string;
    message: string;
  }>;
}

// === MARKETING SOCIAL POST TYPES ===

export interface MarketingSocialPost {
  id: string;
  title: string;
  content: string;
  network: 'facebook' | 'instagram' | 'linkedin' | 'twitter' | 'tiktok';
  status: 'draft' | 'scheduled' | 'published' | 'failed';
  scheduledAt?: string;
  publishedAt?: string;
  media?: Array<{
    type: 'image' | 'video';
    url: string;
    thumbnail?: string;
  }>;
  metrics?: {
    impressions?: number;
    engagements?: number;
    clicks?: number;
    likes?: number;
    shares?: number;
    comments?: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface MarketingPublicationFilters {
  dateRange?: {
    start: string;
    end: string;
    from?: Date;
    to?: Date;
  };
  network?: MarketingSocialPost['network'];
  status?: MarketingSocialPost['status'];
  search?: string;
  propertyId?: string;
  page?: number;
  limit?: number;
}

export interface MarketingPublicationStats {
  total: number;
  byNetwork: Record<string, number>;
  byStatus: Record<string, number>;
  scheduled: number;
  published: number;
  drafts: number;
  engagementRate?: number;
  totalImpressions?: number;
  totalEngagements?: number;
  connectedAccounts?: number;
  scheduledPosts?: number;
  publishedPosts?: number;
  failedPosts?: number;
  bestNetwork?: string;
  bestPropertyTitle?: string;
}

export interface MarketingSchedulerSlot {
  id: string;
  date: string;
  time: string;
  timezone: string;
  available: boolean;
  postsCount?: number;
  maxPosts?: number;
  start?: string;
  end?: string;
}